import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import BrandLogo from './BrandLogo';
import EcranChargement from './EcranChargement';

const AuthModal = () => {
  const { loginWithEmail, error: authError, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Show auth error
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const handleSubmit = async () => {
    setError(null);

    if (!email || !password) {
      setError('Email et mot de passe requis');
      return;
    }
    setIsSubmitting(true);
    const result = await loginWithEmail(email, password);
    if (!result.success) {
      setPassword('');
    }
    setIsSubmitting(false);
  };

  if (isLoading) return <EcranChargement />;

  return (
    // The sign-in screen no longer pulls a stock photo from a remote CDN: this
    // app is used in the field with an offline queue, and the first screen must
    // not depend on the network to render.
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-background p-4">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(90%_60%_at_50%_0%,hsl(var(--primary)/0.10)_0%,transparent_70%)]"
      />
      <div className="noise-overlay pointer-events-none absolute inset-0" />

      {/* Modal */}
      <div
        className="relative w-full max-w-md overflow-hidden rounded-xl border border-border bg-card shadow-lg"
        data-testid="auth-modal"
      >
        {/* The flame appears once, as a hairline: the brand signs the screen
            without shouting over the one action on it. */}
        <div className="h-1 w-full bg-flame" aria-hidden="true" />

        <div className="px-6 pb-6 pt-7 text-center">
          <BrandLogo variant="plain" size={56} className="mx-auto" />
          <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">
            E-CARBU
          </h1>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            Pilotage carburant
          </p>
        </div>

        {/* Body */}
        <div className="px-6 pb-6">
          {/* A real form: browsers can autofill it, password managers can see
              it, and Enter submits without a hand-rolled keydown handler. */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            noValidate
          >
            <div className="mb-5 space-y-4">
                <div>
                  <label htmlFor="auth-email" className="label-base">
                    Email
                  </label>
                  <input
                    id="auth-email"
                    ref={inputRef}
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                    disabled={isSubmitting}
                    placeholder="vous@societe.com"
                    aria-invalid={Boolean(error)}
                    className={cn(
                      'input-base',
                      error && 'border-destructive bg-destructive/5',
                    )}
                    data-testid="auth-email-input"
                  />
                </div>
                <div>
                  <label htmlFor="auth-password" className="label-base">
                    Mot de passe
                  </label>
                  <input
                    id="auth-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                    disabled={isSubmitting}
                    placeholder="••••••••"
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? 'auth-error-msg' : undefined}
                    className={cn(
                      'input-base',
                      error && 'border-destructive bg-destructive/5',
                    )}
                    data-testid="auth-password-input"
                  />
                </div>
              </div>

            {/* Error message */}
            {error && (
              <div
                id="auth-error-msg"
                role="alert"
                className="mb-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-destructive"
                data-testid="auth-error"
              >
                <AlertCircle className="mt-px h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className="w-full"
              data-testid="auth-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connexion…
                </>
              ) : (
                'Se connecter'
              )}
            </Button>
          </form>

          {/* <p id="auth-help" className="mt-4 text-center text-xs text-muted-foreground">
            Identifiants Super Admin ou Administrateur. Les comptes terrain
            (chauffeurs, stations) utilisent l'application mobile.
          </p> */}

          <div className="mt-4 text-center">
            <Link
              to="/privacy-policy"
              className="rounded text-xs font-medium text-primary hover:underline"
            >
              Politique de confidentialité
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
